import React, { useState, useEffect } from 'react';
import { db } from '../../config/firebase';
import { collection, getDocs } from 'firebase/firestore';
import { askGemini } from '../../config/gemini';
import Sidebar from '../Shared/Sidebar';

export default function AIInsights() {
  const [students, setStudents] = useState([]);
  const [parents, setParents] = useState([]);
  const [reports, setReports] = useState([]);
  const [sessions, setSessions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [insights, setInsights] = useState(null);
  const [studentRatings, setStudentRatings] = useState([]);
  const [parentInsights, setParentInsights] = useState([]);
  const [activeTab, setActiveTab] = useState('overview');

  useEffect(() => {
    fetchData();
  }, []);

  async function fetchData() {
    try {
      const studentsSnap = await getDocs(collection(db, 'students'));
      const parentsSnap = await getDocs(collection(db, 'parents'));
      const reportsSnap = await getDocs(collection(db, 'reports'));
      const sessionsSnap = await getDocs(collection(db, 'sessions'));
      setStudents(studentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setParents(parentsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setReports(reportsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
      setSessions(sessionsSnap.docs.map(d => ({ id: d.id, ...d.data() })));
    } catch (err) {
      console.error(err);
    }
  }

  async function generateOverviewInsights() {
    setLoading(true);
    try {
      const prompt = `
You are an AI analyst for Iyanna Foundation, a Tanzania NGO that mentors children primary school children Classes 1 to 7.

Analyze this program data and provide detailed insights:

PROGRAM DATA:
- Total Students: ${students.length}
- Total Parents: ${parents.length}
- Total Sessions: ${sessions.length}
- Total Reports: ${reports.length}

STUDENT PROGRESS SUMMARY:
${students.slice(0, 10).map(s => `
- ${s.firstName} ${s.lastName} (Class ${s.class}):
  Academic: ${s.progress?.academic || 0}%
  Behaviour: ${s.progress?.behaviour || 0}%
  Health: ${s.progress?.health || 0}%
  Community: ${s.progress?.community || 0}%
  Life Skills: ${s.progress?.lifeSkills || 0}%
`).join('')}

BIRTH CERTIFICATES:
- Have certificate: ${students.filter(s => s.hasBirthCertificate === 'yes').length}
- Need certificate: ${students.filter(s => s.hasBirthCertificate === 'no').length}
- In process: ${students.filter(s => s.hasBirthCertificate === 'processing').length}

MICROFINANCE:
- Active parents: ${parents.filter(p => p.capitalAmount).length}

Please provide:
1. Overall program health assessment
2. Key strengths of the program
3. Areas needing immediate attention
4. Specific recommendations for improvement
5. Predictions for next month

Format your response with clear sections and bullet points. Be specific and actionable.
`;
      const result = await askGemini(prompt);
      setInsights(result);
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function generateStudentRatings() {
    setLoading(true);
    try {
      const prompt = `
You are an AI student progress analyst for Iyanna Foundation Tanzania.

Analyze these students and rate each one:

${students.slice(0, 15).map(s => `
Student: ${s.firstName} ${s.lastName}
Class: ${s.class}
School: ${s.school}
Academic: ${s.progress?.academic || 0}%
Behaviour: ${s.progress?.behaviour || 0}%
Health: ${s.progress?.health || 0}%
Community: ${s.progress?.community || 0}%
Life Skills: ${s.progress?.lifeSkills || 0}%
Health Status: ${s.healthStatus || 'Unknown'}
---`).join('')}

For each student provide a JSON array with this exact format:
[
  {
    "name": "Student Name",
    "rating": "Excellent/Good/Average/Needs Support/Critical",
    "trend": "Improving/Stable/Declining",
    "score": 85,
    "reason": "Brief reason in one sentence",
    "recommendation": "One specific action to take"
  }
]

Return ONLY the JSON array, nothing else.
`;
      const result = await askGemini(prompt);
      try {
        const clean = result.replace(/```json|```/g, '').trim();
        const parsed = JSON.parse(clean);
        setStudentRatings(parsed);
      } catch (e) {
        console.error('Parse error:', e);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  async function generateParentInsights() {
    setLoading(true);
    try {
      const prompt = `
You are an AI family support analyst for Iyanna Foundation Tanzania.

Analyze these parent/family situations:

${parents.slice(0, 10).map(p => `
Parent: ${p.firstName} ${p.lastName}
Business: ${p.businessType || 'None'}
Capital Given: TSh ${p.capitalAmount || 0}
RA Sessions Attended: ${[p.sessions?.raSession1, p.sessions?.raSession2].filter(Boolean).length}/2
Microfinance Training: ${p.sessions?.microfinanceTraining ? 'Yes' : 'No'}
Children Enrolled: ${p.childrenEnrolled || 0}
---`).join('')}

For each parent provide a JSON array:
[
  {
    "name": "Parent Name",
    "status": "Thriving/Progressing/Needs Support/At Risk",
    "businessHealth": "Strong/Growing/Struggling/Not Started",
    "engagementLevel": "High/Medium/Low",
    "reason": "Brief assessment in one sentence",
    "recommendation": "One specific action"
  }
]

Return ONLY the JSON array, nothing else.
`;
      const result = await askGemini(prompt);
      try {
        const clean = result.replace(/```json|```/g, '').trim();
        const parsed = JSON.parse(clean);
        setParentInsights(parsed);
      } catch (e) {
        console.error('Parse error:', e);
      }
    } catch (err) {
      console.error(err);
    }
    setLoading(false);
  }

  function getRatingColor(rating) {
    switch (rating) {
      case 'Excellent': return { bg: '#EAF3EC', color: '#14452F' };
      case 'Good': return { bg: '#EAF3EC', color: '#1D5C3C' };
      case 'Average': return { bg: '#FFF3E0', color: '#E65100' };
      case 'Needs Support': return { bg: '#FFF3E0', color: '#BF360C' };
      case 'Critical': return { bg: '#FFEBEE', color: '#C62828' };
      case 'Thriving': return { bg: '#EAF3EC', color: '#14452F' };
      case 'Progressing': return { bg: '#E3F2FD', color: '#1565C0' };
       return { bg: '#FFF3E0', color: '#E65100' };
      case 'At Risk': return { bg: '#FFEBEE', color: '#C62828' };
      default: return { bg: '#F3F4F6', color: '#6B7280' };
    }
  }

  function getTrendIcon(trend) {
    if (trend === 'Improving') return '';
    if (trend === 'Declining') return '📉';
    return '';
  }

  return (
    <div style={styles.layout}>
      <Sidebar />
      <div style={styles.main}>

        <div style={styles.pageHeader}>
          <div>
            <div style={styles.pageTitle}>
              AI Insights
            </div>
            <div style={styles.pageSub}>
              AI powered analysis of your program data
            </div>
          </div>
        </div>

        {/* TABS */}
        <div style={styles.tabs}>
          {[
            { id: 'overview', label: 'Program Overview' },
            { id: 'students', label: 'Student Ratings' },
            { id: 'parents', label: 'Parent Insights' },
          ].map(tab => (
            <button
              key={tab.id}
              style={{
                ...styles.tab,
                ...(activeTab === tab.id ? styles.tabActive : {})
              }}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* OVERVIEW TAB */}
        {activeTab === 'overview' && (
          <div>
            <div style={styles.statsRow}>
              {[
                { emoji: '', value: students.length, label: 'Students', color: '#14452F' },
                { emoji: '', value: parents.length, label: 'Parents', color: '#F57C00' },
                { emoji: '', value: sessions.length, label: 'Sessions', color: '#4527A0' },
                { emoji: '', value: reports.length, label: 'Reports', color: '#00695C' },
              ].map((s, i) => (
                <div key={i} style={styles.statCard}>
                  <div style={styles.statEmoji}>{s.emoji}</div>
                  <div style={{ ...styles.statVal, color: s.color }}>
                    {s.value}
                  </div>
                  <div style={styles.statLbl}>{s.label}</div>
                </div>
              ))}
            </div>

            {!insights ? (
              <div style={styles.generateCard}>
                <div style={styles.generateIcon}></div>
                <div style={styles.generateTitle}>
                  Generate AI Program Analysis
                </div>
                <div style={styles.generateDesc}>
                  AI will analyze all your program data and give you detailed insights, recommendations and predictions
                </div>
                <button
                  style={styles.generateBtn}
                  onClick={generateOverviewInsights}
                  disabled={loading}
                >
                  {loading
                    ? '⟳ Analyzing...'
                    : 'Generate Insights'}
                </button>
              </div>
            ) : (
              <div style={styles.insightsCard}>
                <div style={styles.insightsHeader}>
                  <div style={styles.insightsTitle}>
                    AI Program Analysis
                  </div>
                  <button
                    style={styles.refreshBtn}
                    onClick={generateOverviewInsights}
                    disabled={loading}
                  >
                    {loading ? '⟳' : 'Refresh'}
                  </button>
                </div>
                <div style={styles.insightsText}>
                  {insights}
                </div>
              </div>
            )}
          </div>
        )}

        {/* STUDENTS TAB */}
        {activeTab === 'students' && (
          <div>
            {studentRatings.length === 0 ? (
              <div style={styles.generateCard}>
                <div style={styles.generateIcon}></div>
                <div style={styles.generateTitle}>
                  AI Student Progress Rating
                </div>
                <div style={styles.generateDesc}>
                  AI will rate each student as Excellent, Good, Average, Needs Support or Critical based on their progress data
                </div>
                <button
                  style={styles.generateBtn}
                  onClick={generateStudentRatings}
                  disabled={loading}
                >
                  {loading
                    ? '⟳ Analyzing Students...'
                    : 'Rate All Students'}
                </button>
              </div>
            ) : (
              <div>
                <div style={styles.refreshRow}>
                  <div style={styles.insightsTitle}>
                    Student Ratings ({studentRatings.length})
                  </div>
                  <button
                    style={styles.refreshBtn}
                    onClick={generateStudentRatings}
                    disabled={loading}
                  >
                    {loading ? '⟳' : 'Refresh'}
                  </button>
                </div>
                {studentRatings.map((s, i) => {
                  const colors = getRatingColor(s.rating);
                  return (
                    <div key={i} style={styles.ratingCard}>
                      <div style={styles.ratingHeader}>
                        <div style={styles.ratingAvatar}>
                          {s.name?.[0]}
                        </div>
                        <div style={styles.ratingInfo}>
                          <div style={styles.ratingName}>
                            {s.name}
                          </div>
                          <div style={styles.ratingScore}>
                            Score: {s.score}%
                          </div>
                        </div>
                        <div style={styles.ratingBadges}>
                          <span style={{
                            ...styles.ratingBadge,
                            background: colors.bg,
                            color: colors.color,
                          }}>
                            {s.rating}
                          </span>
                          <span style={styles.trendBadge}>
                            {getTrendIcon(s.trend)} {s.trend}
                          </span>
                        </div>
                      </div>
                      <div style={styles.ratingReason}>
                        {s.reason}
                      </div>
                      <div style={styles.ratingRecommendation}>
                        Action: {s.recommendation}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* PARENTS TAB */}
        {activeTab === 'parents' && (
          <div>
            {parentInsights.length === 0 ? (
              <div style={styles.generateCard}>
                <div style={styles.generateIcon}></div>
                <div style={styles.generateTitle}>
                  AI Parent & Family Insights
                </div>
                <div style={styles.generateDesc}>
                  AI will analyze each family's situation and give status ratings and recommendations
                </div>
                <button
                  style={styles.generateBtn}
                  onClick={generateParentInsights}
                  disabled={loading}
                >
                  {loading
                    ? '⟳ Analyzing Families...'
                    : 'Analyze All Families'}
                </button>
              </div>
            ) : (
              <div>
                <div style={styles.refreshRow}>
                  <div style={styles.insightsTitle}>
                    Family Insights ({parentInsights.length})
                  </div>
                  <button
                    style={styles.refreshBtn}
                    onClick={generateParentInsights}
                    disabled={loading}
                  >
                    {loading ? '⟳' : 'Refresh'}
                  </button>
                </div>
                {parentInsights.map((p, i) => {
                  const colors = getRatingColor(p.status);
                  return (
                    <div key={i} style={styles.ratingCard}>
                      <div style={styles.ratingHeader}>
                        <div style={{
                          ...styles.ratingAvatar,
                          background: 'linear-gradient(135deg, #F57C00, #FFA726)'
                        }}>
                          {p.name?.[0]}
                        </div>
                        <div style={styles.ratingInfo}>
                          <div style={styles.ratingName}>
                            {p.name}
                          </div>
                          <div style={styles.ratingScore}>
                            Business: {p.businessHealth}
                          </div>
                        </div>
                        <div style={styles.ratingBadges}>
                          <span style={{
                            ...styles.ratingBadge,
                            background: colors.bg,
                            color: colors.color,
                          }}>
                            {p.status}
                          </span>
                          <span style={styles.trendBadge}>
                            Engagement: {p.engagementLevel}
                          </span>
                        </div>
                      </div>
                      <div style={styles.ratingReason}>
                        {p.reason}
                      </div>
                      <div style={styles.ratingRecommendation}>
                        Action: {p.recommendation}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

      </div>
    </div>
  );
}

const styles = {
  layout: {
    display: 'flex',
    minHeight: '100vh',
    background: '#F6F4EE',
  },
  main: {
    flex: 1,
    padding: '26px',
    overflowY: 'auto',
  },
  pageHeader: {
    marginBottom: '24px',
  },
  pageTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '25px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
  },
  pageSub: {
    fontSize: '12.5px',
    color: '#6B7280',
    marginTop: '3px',
  },
  tabs: {
    display: 'flex',
    gap: '8px',
    marginBottom: '20px',
    flexWrap: 'wrap',
  },
  tab: {
    padding: '10px 18px',
    borderRadius: '20px',
    border: '1.5px solid #E5E7EB',
    background: '#fff',
    color: '#6B7280',
    fontSize: '13px',
    cursor: 'pointer',
    fontFamily: 'inherit',
    fontWeight: '500',
  },
  tabActive: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    borderColor: 'transparent',
    color: '#fff',
  },
  statsRow: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))',
    gap: '12px',
    marginBottom: '20px',
  },
  statCard: {
    background: '#fff',
    borderRadius: '14px',
    padding: '16px',
    textAlign: 'center',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  statEmoji: { fontSize: '24px', marginBottom: '8px' },
  statVal: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '28px',
    fontWeight: '700',
  },
  statLbl: { fontSize: '11px', color: '#6B7280', marginTop: '2px' },
  generateCard: {
    background: '#fff',
    borderRadius: '20px',
    padding: '48px 32px',
    textAlign: 'center',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  generateIcon: { fontSize: '56px', marginBottom: '16px' },
  generateTitle: {
    fontFamily: "'Fraunces', Georgia, serif",
    fontSize: '20px',
    color: '#1C1C1E',
    marginBottom: '10px',
  },
  generateDesc: {
    fontSize: '13.5px',
    color: '#6B7280',
    lineHeight: 1.6,
    maxWidth: '400px',
    margin: '0 auto 24px',
  },
  generateBtn: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    color: '#fff',
    border: 'none',
    padding: '14px 32px',
    borderRadius: '12px',
    fontFamily: 'inherit',
    fontSize: '14px',
    fontWeight: '600',
    cursor: 'pointer',
    boxShadow: '0 6px 18px rgba(20,69,47,0.3)',
  },
  insightsCard: {
    background: '#fff',
    borderRadius: '16px',
    overflow: 'hidden',
    boxShadow: '0 2px 10px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
  },
  insightsHeader: {
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    padding: '16px 20px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  insightsTitle: {
    fontSize: '15px',
    fontWeight: '600',
    color: '#fff',
  },
  refreshBtn: {
    background: 'rgba(255,255,255,0.15)',
    color: '#fff',
    border: '1px solid rgba(255,255,255,0.25)',
    padding: '6px 14px',
    borderRadius: '8px',
    fontSize: '12px',
    fontWeight: '600',
    cursor: 'pointer',
    fontFamily: 'inherit',
  },
  insightsText: {
    padding: '20px',
    fontSize: '13.5px',
    color: '#374151',
    lineHeight: 1.7,
    whiteSpace: 'pre-wrap',
  },
  refreshRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '16px',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    padding: '14px 18px',
    borderRadius: '12px',
  },
  ratingCard: {
    background: '#fff',
    borderRadius: '14px',
    padding: '16px',
    boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
    border: '1px solid rgba(0,0,0,0.04)',
    marginBottom: '12px',
  },
  ratingHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
    marginBottom: '10px',
    flexWrap: 'wrap',
  },
  ratingAvatar: {
    width: '40px',
    height: '40px',
    borderRadius: '50%',
    background: 'linear-gradient(135deg, #14452F, #1F6B45)',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '16px',
    fontWeight: '600',
    color: '#fff',
    flexShrink: 0,
  },
  ratingInfo: { flex: 1 },
  ratingName: {
    fontSize: '14px',
    fontWeight: '600',
    color: '#1C1C1E',
  },
  ratingScore: {
    fontSize: '12px',
    color: '#6B7280',
    marginTop: '2px',
  },
  ratingBadges: {
    display: 'flex',
    gap: '6px',
    flexWrap: 'wrap',
  },
  ratingBadge: {
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '20px',
    fontWeight: '600',
  },
  trendBadge: {
    fontSize: '11px',
    padding: '4px 10px',
    borderRadius: '20px',
    background: '#F3F4F6',
    color: '#6B7280',
    fontWeight: '500',
  },
  ratingReason: {
    fontSize: '12.5px',
    color: '#374151',
    lineHeight: 1.5,
    marginBottom: '6px',
    padding: '8px 12px',
    background: '#F9FAFB',
    borderRadius: '8px',
  },
  ratingRecommendation: {
    fontSize: '12.5px',
    color: '#14452F',
    lineHeight: 1.5,
    padding: '8px 12px',
    background: '#EAF3EC',
    borderRadius: '8px',
  },
};